/* eslint-disable @typescript-eslint/no-explicit-any */
import { RegisterPage } from './new/pages/auth/register.page';
import { LoginPage } from './new/pages/auth/login.page';
import { StripeCallbackPage } from './new/pages/influencer/stripe-callback.page';
import {
  withRouter,
  browserHistory,
  RouteConfig,
  RouteComponent,
} from 'react-router';
import auth from './components/auth';
import Application from './Application';
import { NewCollectionPage } from './components/collections/NewCollectionPage';
import ConfigFileEditor from './components/ConfigFileEditor';
import Dashboard from './components/Dashboard';
import InfluencerList from './pages/admin/InfluencerList.page';
import {
  AnalyticsOverview,
  PlaybookAdminAnalyticsOverview,
} from './pages/metabase/AnalyticsOverview';
import PortalUserEdit from './components/PortalUserEdit';
import PortalUserList from './components/PortalUserList';
import { WorkoutCommentList } from './components/WorkoutCommentList';
import { isLoggedIn } from './new/stores/user.store';
import { OTPLandingPageBuilder } from './pages/CustomProgramPage/OTPLandingPageBuilder';
import { CreatorCrmPage } from './new/pages/crm/CrmUsers.page';
import { InboxPage } from './new/pages/inbox/Inbox.page';
import { LiveStreamPage } from './new/pages/livestream/Livestream.page';
import { NotFoundPage } from './pages/NotFoundPage';
import { ArchivedWorkoutsPage } from './pages/partner/workouts/ArchivedWorkoutsPage';
import { DraftWorkoutsPage } from './pages/partner/workouts/DraftWorkoutsPage';
import { PublishedWorkoutsPage } from './pages/partner/workouts/PublishedWorkoutsPage';
import { ScheduledWorkoutsPage } from './pages/partner/workouts/ScheduledWorkoutsPage';
// import { HomePage } from './new/pages/influencer/home/home.page';
import ProfilePage from './new/pages/creator/profile/Profile.page';
import {
  withLegacyBackground,
  withLegacyStyles,
  withLegacyTweakedStyles,
  wrappedWithLegacyBackground,
} from './new/pages/legacy-styles.hoc';
import { LandingPageBuilder } from './new/pages/LandingPagesBuilder/LandingPageBuilder';
import { CommunityPage } from './new/community/communityPage';
import { PostPage } from './new/community/postPage';
import { CommunityInitPage } from './new/community/communityInitPage';
import { withSettingsLayout } from './new/pages/creator/settings/CreatorSettingsLayout';
import CreatorAccount from './new/pages/creator/settings/account/CreatorAccount.page';
import {
  OTPListPage,
  SubscriptionListPage,
} from './new/pages/collections/collections.page';
import DomainSetup from './new/pages/creator/settings/domain/DomainSetup/DomainSetup.page';
import { resolveDashboardRedirect } from './new/pages/auth/model/registration.model';
import {
  LOGIN_PATH as AUTH_LOGIN_PATH,
  REGISTRATION_PATH as AUTH_REGISTRATION_PATH,
  authPath,
} from './new/pages/auth/model/auth.paths.model';
import { CrmWebhooksPage } from './new/pages/crm/CrmWebhooks.page';
import { MarketingPage } from './new/pages/Marketing/Marketing.page';
import { PayoutsPage } from './new/pages/creator/payouts/PayoutsPage';
import { Administration } from './new/pages/admin/Administration.page';
import { InfluencerListNewPage } from './new/pages/admin/influencer-list-new/InfluencerListNew.page';
import { NewCreator } from './new/pages/newCreator/NewCreator.page';
import { SettingsPage } from './new/pages/settings/Settings.page';
import { HomePage } from './new/pages/creator/home/Home.page';
import { CollectionPage } from './new/pages/collection/CollectionPage';
import {
  ProgramTabHash,
  SalesType,
  WorkoutsTabHash,
} from './new/pages/collection/domain';
import { MailSubscription } from './new/pages/creator/mail-subscription/MailSubscription.page';
import { PromotionsPage } from './new/pages/promotions/Promotions.page';
import { LibraryPage } from './new/pages/creator/library/views/library.page';
import { QueryParamsSchema } from '@client/new/pages/promotions/view-model/discounts.viewModel';
import { PromotionsDetailsPage } from './new/pages/promotions/PromotionDetails.page';
import { ChallengeDetailsPage } from './new/pages/challenges/ChallengeDetails.page';
import { EditWorkoutPage } from './new/pages/workout/WorkoutEdit';
import { ExtrasPage } from './new/pages/creator/extras/Extras.page';
import { BundlesManagementPage } from './new/pages/bundles/BundlesManagement.page';
import { OfferLandingPageBuilder } from './pages/CustomProgramPage/OfferLandingPage';
import { GuidePage } from './new/pages/creator/guide/Guide.page';
import { OnboardingPage } from './new/pages/onboarding/Onboarding.page';
import { CreatorLoginWithPasswordPage } from '@client/new/pages/auth/login/pages/login-with-password.page';

function redirectToLogin(nextState, replace) {
  if (!isLoggedIn()) {
    replace({
      pathname: '/login',
      state: { nextPathname: nextState.location.pathname },
    });
    return true;
  }
  return false;
}

function redirectToDashboard(_nextState, replace) {
  const redirectDecision = resolveDashboardRedirect({
    isLoggedIn: isLoggedIn(),
  });

  if (redirectDecision === 'home') {
    replace('/home');
    return true;
  }

  return false;
}

const buildQueryParams = <T extends Record<string, string>>(p: T): string => {
  if (!p || !Object.keys(p).length) return '';
  const s = new URLSearchParams(p);
  return '?' + s.toString();
};

type InfluencerID = number | ':influencerId';
type ChallengeID = number | ':challengeId';
type ProgramID = number | ':programId';

const PROMOTIONS_PATH = (influencerId: InfluencerID) =>
  `/creator/${influencerId}/promotions`;

const PROMOTIONS = (influencerId: InfluencerID, params?: QueryParamsSchema) =>
  `${PROMOTIONS_PATH(influencerId)}${buildQueryParams(params)}`;

const PROMOTIONS_DETAILS = (
  influencerId: InfluencerID,
  discountId: number | ':discountId',
) => `${PROMOTIONS_PATH(influencerId)}/${discountId}`;

const CHALLENGE_DETAILS_PATH = (p: {
  influencerId: InfluencerID;
  programId: ProgramID;
  challengeId: ChallengeID;
}) =>
  `/creator/${p.influencerId}/program/${p.programId}/challenge/${p.challengeId}`;

const CHALLENGE_DETAILS = (
  influencerId: InfluencerID,
  programId: ProgramID,
  challengeId: ChallengeID,
  params?: QueryParamsSchema,
) =>
  `${CHALLENGE_DETAILS_PATH({
    influencerId,
    programId,
    challengeId,
  })}${buildQueryParams(params)}`;

export const PATHS = {
  HOME: '/home',
  HOME_CREATOR: (id: number | ':influencerId') => `/admin/home/${id}`,
  LOGIN: AUTH_LOGIN_PATH,
  NEW_SIGN_IN: authPath('/new/sign-in'),
  LOGIN_CONFIRMATION_CODE: authPath('/login/confirmation-code'),
  ADMIN_LOGIN_CONFIRMATION_CODE: '/login/admin-confirmation-code',
  LOGIN_WITH_PASSWORD: '/login-with-password',
  NEW_SIGN_UP: authPath('/new/signup'),
  NEW_SIGN_UP_DASHED: authPath('/new/sign-up'),
  SIGN_UP: authPath('/signup'),
  SIGN_UP_FILL_NAME: `${AUTH_REGISTRATION_PATH}/fill-name`,
  SIGN_UP_REFERRAL_SOURCE: '/sign-up/referral-source',
  SIGN_UP_INSTAGRAM: `${AUTH_REGISTRATION_PATH}/instagram`,
  SIGN_UP_PHONE: `${AUTH_REGISTRATION_PATH}/phone`,
  SIGN_UP_SALES_MODEL: '/sign-up/sales-model',
  SIGN_UP_CONFIRMATION_CODE: `${AUTH_REGISTRATION_PATH}/confirmation-code`,
  STRIPE_CALLBACK: '/stripe-callback',
  WORKOUTS: '/admin/workouts',
  EXERCISE_LIBRARY: '/admin/exercise-library',
  COLLECTIONS: '/collections',
  VIDEO_LIBRARY: '/admin/video-library',
  PARTNET_VIDEO_LIBRARY: (influencerId) =>
    `/admin/partner/${influencerId}/video-library`,
  LIBRARY: (influencerId) => `/creator/${influencerId}/library`,
  CREATOR_PROFILE: '/creator/profile',
  PARTNER_EDIT: (id) => `/admin/partner/${id}/edit`,
  PARTNER_SETTINGS: (id) => `/admin/partner/${id}/settings`,
  PARTNER_FEED_SETTINGS: (id) => `/admin/partner/${id}/feed-settings`,
  PARTNER_EXERCISE_LIBRARY: (id: number | ':influencerId') =>
    `/admin/partner/${id}/exercise-library`,
  PARTNER_VIDEO_LIBRARY: (id: number | ':influencerId') =>
    `/admin/partner/${id}/video-library`,
  ANALYTICS: '/admin/analytics',
  PARTNER_ANALYTICS: (influencerId: number | ':influencerId') =>
    `/admin/partner/${influencerId}/analytics`,
  LANDING_PAGE: (creatorId: number | string) =>
    `/creator/${creatorId}/landing-page`,
  CRM_CREATOR: (
    creatorId: number | ':influencerId',
    tab: 'users' | 'webhooks',
  ) => `/creator/${creatorId}/crm/${tab}`,
  MARKETING: (influencerId: number | ':influencerId') =>
    `/creator/${influencerId}/marketing`,
  PROMOTIONS,
  CHALLENGE_DETAILS,
  PROMOTIONS_DETAILS,
  PURCHASE_PAGE: (influencerId, programId) =>
    `/admin/partner/${influencerId}/purchase-page/${programId}`,
  CONFIG_FILE: '/admin/config-file',
  PARTNER_WORKOUTS: (influencerId: number | ':influencerId') =>
    `/admin/partner/${influencerId}/workouts`,
  PARTNER_WORKOUTS_DRAFTS: (id: number | ':influencerId') =>
    `/admin/partner/${id}/workouts/drafts`,
  PARTNER_WORKOUTS_SCHEDULED: (id: number | ':influencerId') =>
    `/admin/partner/${id}/workouts/scheduled`,
  PARTNER_WORKOUTS_PUBLISHED: (id: number | ':influencerId') =>
    `/admin/partner/${id}/workouts/published`,
  PARTNER_COLLECTION_WITH_REFRESH: (
    id: number | ':influencerId',
    collectionId,
  ) => `/admin/partner/${id}/workouts/published/${collectionId}?refresh=true`,
  PARTNER_WORKOUTS_PUBLISHED_PROGRAM: (
    id: number | ':influencerId',
    programId,
  ) => `/admin/partner/${id}/workouts/published/${programId}`,
  PARTNER_WORKOUTS_ARCHIVED: (id: number | ':influencerId') =>
    `/admin/partner/${id}/workouts/archived`,
  WORKOUT_COMMENTS: (id, influencerId: number | ':influencerId') =>
    `/admin/${influencerId}/workout/${id}/comments`,
  WORKOUT_EDIT: ({
    workoutId,
    influencerId,
    programId,
    fromWorkoutTab,
    view,
  }: {
    workoutId: number | ':id';
    influencerId: number | ':influencerId';
    programId?: number | null;
    fromWorkoutTab?: WorkoutsTabHash;
    view?: string;
  }) => {
    const search = [
      { key: 'programId', value: programId },
      { key: 'fromWorkoutTab', value: fromWorkoutTab },
      { key: 'view', value: view },
    ]
      .filter((i) => i.value)
      .map((i) => `${i.key}=${i.value}`)
      .join('&');
    return `/admin/partner/${influencerId}/workout/${workoutId}/edit${
      search ? '?' + search : ''
    }`;
  },
  CLASS_EDIT: ({
    workoutId,
    influencerId,
    programId,
    fromWorkoutTab,
    view,
  }: {
    workoutId: number | ':id';
    influencerId: number | ':influencerId';
    programId?: number | null;
    fromWorkoutTab?: WorkoutsTabHash;
    view?: string;
  }) => {
    const search = [
      { key: 'programId', value: programId },
      { key: 'fromWorkoutTab', value: fromWorkoutTab },
      { key: 'view', value: view },
    ]
      .filter((i) => i.value)
      .map((i) => `${i.key}=${i.value}`)
      .join('&');
    return `/admin/partner/${influencerId}/class/${workoutId}/edit${
      search ? '?' + search : ''
    }`;
  },
  NEW_PROGRAM: (
    influencerId: number,
    params?: { salesType?: SalesType; from?: string },
  ) =>
    `/admin/${influencerId}/new-program` +
    (params ? `?${new URLSearchParams(params).toString()}` : ''),
  PROGRAM_EDIT: (
    programId: number | ':programId',
    influencerId: number | ':influencerId',
    tab?: ProgramTabHash,
  ) =>
    `/admin/${influencerId}/program/${programId}/edit` + (tab ? '#' + tab : ''),
  NEW_CATEGORY: (influencerId: number, params?: { from?: string }) =>
    `/admin/${influencerId}/new-category` +
    (params ? `?${new URLSearchParams(params).toString()}` : ''),
  CATEGORY_EDIT: (
    id: number | ':programId',
    influencerId: number | ':influencerId',
  ) => `/admin/${influencerId}/category/${id}/edit`,
  WORKOUT_VIEW: (id) => `/admin/workout/${id}/view`,
  PORTAL_USERS: '/admin/portal-users',
  PORTAL_USERS_CREATE: '/admin/portal-users/create',
  PORTAL_USERS_EDIT: (id) => `/admin/portal-users/${id}/edit`,
  ADMIN_NEW_CREATOR: '/admin/new-creator',
  LIVESTREAM: '/admin/livestream',
  LIVESTREAM_UPCOMING: '/admin/livestream/upcoming',
  LIVESTREAM_PAST: '/admin/livestream/past',
  INBOX: (userId?: number) => {
    return `/inbox${userId ? '?userId=' + userId : ''}`;
  },
  INBOX_MESSAGE: (influencerId: number | string, userId?: number) =>
    `/creator/${influencerId}/inbox${userId ? '?userId=' + userId : ''}`,
  LIVESTREAM_VIEW: (influencerId) => `/admin/livestream/${influencerId}`,
  COMMUNITY_INIT: (influencerId) => `/creator/${influencerId}/community`,
  COMMUNITY_INIT_ADMIN: (influencerId) =>
    `/admin/partner/${influencerId}/community`,
  COMMUNITY: (influencerId, communityId) =>
    `/creator/${influencerId}/community/${communityId}`,
  COMMUNITY_POST: (influencerId, communityId, postId) =>
    `/creator/${influencerId}/community/${communityId}/post/${postId}`,
  PURCHASES: (influencerId?: number) => `/otps/${influencerId}`,
  SUBSCRIPTIONS: (influencerId?: number, openSettings?: boolean) =>
    `/subscriptions/${influencerId}${openSettings ? '?openSettings' : ''}`,
  CREATOR: {
    PROFILE: (strictForm = false) =>
      `/creator/account/profile${strictForm ? '?strictForm' : ''}`,
    SETTINGS: '/creator/account/settings',
  },
  ADMIN_PAYMENT: (influencerId: number | string) =>
    `/admin/partner/${influencerId}/payment`,
  CUSTOM_DOMAIN_SETUP: (influencerId: number | ':influencerId') =>
    `/creator/${influencerId}/setup-domain`,
  ADMIN_ADMINISTRATION: (influencerId: number | string) =>
    `/admin/partner/${influencerId}/admin`,
  contentLibrary: (influencerId: string | number) =>
    `/creator/${influencerId}/videos`,
  EXTRAS: (influencerId: number | ':influencerId') =>
    `/creator/${influencerId}/extras`,
  BUNDLES: (influencerId: number | ':influencerId', offerId?: number) =>
    `/creator/${influencerId}/offers` + (offerId ? '#' + offerId : ''),
  OFFER_LANDING_PAGE_EDITOR: (
    influencerId: number | ':influencerId',
    offerId: number | ':offerId',
  ) => `/creator/${influencerId}/offers/${offerId}/landing-page`,
  GUIDE: (influencerId: number | ':influencerId') =>
    `/creator/${influencerId}/guide`,
  ONBOARDING: '/onboarding',
  ONBOARDING_ADMIN: (influencerId: number | ':influencerId') =>
    `/admin/${influencerId}/onboarding`,
};

const OnboardingPageWithLegacyBackground = withLegacyBackground(OnboardingPage);
export const routes: RouteConfig = {
  component: Application,
  childRoutes: [
    {
      path: '/',
      onEnter: (_, replace) => replace(isLoggedIn() ? PATHS.HOME : PATHS.LOGIN),
    },
    {
      path: '/mail-subscription/:email',
      getComponent: (_, cb) => {
        cb(null, MailSubscription as RouteComponent);
      },
    },
    {
      path: PATHS.ONBOARDING,
      onEnter: redirectToLogin,
      getComponent: (_, cb) => {
        cb(null, OnboardingPageWithLegacyBackground);
      },
    },
    {
      // Admin onboarding - fullscreen, no dashboard layout
      path: '/admin/:influencerId/onboarding',
      onEnter: redirectToLogin,
      getComponent: (_, cb) => {
        cb(null, OnboardingPageWithLegacyBackground);
      },
    },
    {
      onEnter: redirectToDashboard,
      childRoutes: [
        // Unauthenticated routes
        // Redirect to dashboard if user is already logged in
        {
          path: PATHS.LOGIN,
          getComponent: (nextState, cb) => {
            return cb(null, LoginPage as RouteComponent);
          },
        },
        {
          path: PATHS.LOGIN_CONFIRMATION_CODE,
          getComponent: (nextState, cb) => {
            return cb(null, RegisterPage as RouteComponent);
          },
        },
        {
          path: '/login-with-password',
          getComponent: (nextState, cb) => {
            return cb(
              null,
              withLegacyBackground(
                withLegacyStyles(withRouter(CreatorLoginWithPasswordPage)),
              ),
            );
          },
        },
        {
          path: PATHS.SIGN_UP,
          getComponent: (nextState, cb) => {
            return cb(null, RegisterPage as RouteComponent);
          },
        },
        {
          path: AUTH_REGISTRATION_PATH,
          onEnter: (_, replace) => replace(PATHS.SIGN_UP),
        },
        {
          path: PATHS.SIGN_UP_FILL_NAME,
          getComponent: (nextState, cb) => {
            return cb(null, RegisterPage as RouteComponent);
          },
        },
        {
          path: PATHS.SIGN_UP_INSTAGRAM,
          getComponent: (nextState, cb) => {
            return cb(null, RegisterPage as RouteComponent);
          },
        },
        {
          path: PATHS.SIGN_UP_PHONE,
          getComponent: (nextState, cb) => {
            return cb(null, RegisterPage as RouteComponent);
          },
        },
        {
          path: '/sign-up/referral-source',
          onEnter: (_, replace) => replace(PATHS.SIGN_UP),
        },
        {
          path: '/sign-up/sales-model',
          onEnter: (_, replace) => replace(PATHS.SIGN_UP),
        },
        {
          path: PATHS.SIGN_UP_CONFIRMATION_CODE,
          getComponent: (nextState, cb) => {
            return cb(null, RegisterPage as RouteComponent);
          },
        },
        {
          path: PATHS.NEW_SIGN_UP,
          onEnter: (_, replace) => replace(PATHS.SIGN_UP),
        },
        {
          path: PATHS.NEW_SIGN_UP_DASHED,
          onEnter: (_, replace) => replace(PATHS.SIGN_UP),
        },
        {
          path: PATHS.NEW_SIGN_IN,
          onEnter: (_, replace) => replace(PATHS.LOGIN),
        },
      ],
    },
    {
      onEnter: (nextState, replace) => {
        if (!redirectToLogin(nextState, replace) && !auth.isAdministrator()) {
          replace({
            pathname: '/home',
            state: { nextPathname: nextState.location.pathname },
          });
        }
      },
      path: '/home/:influencerId',
      getComponent: (ns, cb) =>
        auth.isAdministrator() ? HomePage : redirectToDashboard(ns, cb),
    },

    {
      onEnter: redirectToLogin,

      path: '/home',
      getComponent: (nextState, cb) => {
        return cb(null, Dashboard as RouteComponent);
      },
      indexRoute: {
        getComponent: (nextState, cb) => {
          if (
            auth.isAdministrator() ||
            auth.isModerator() ||
            auth.isContentEditor()
          ) {
            console.log(
              '[Router] Displaying InfluencerListNewPage component..',
            );
            return cb(null, InfluencerListNewPage);
          } else {
            console.log('[Router] Display PublishedWorkoutsPage component..');
            return cb(null, HomePage);
          }
        },
      },

      childRoutes: [
        {
          path: '/admin/home/:influencerId',
          getComponent: (ns, cb) => {
            return cb(null, HomePage);
          },
        },
        {
          path: '/collections',
          getComponent: (nextState, cb) => {
            return cb(null, withLegacyStyles(PublishedWorkoutsPage));
          },
        },
        {
          path: '/stripe-callback',
          getComponent: (nextState, cb) => {
            console.log('[Router] Displaying StripeCallback component..');
            return cb(
              null,
              withLegacyBackground(StripeCallbackPage as RouteComponent),
            );
          },
        },
        {
          //backward compatibility
          path: '/admin', // TODLO influencerId
          getComponent: (nextState, cb) => {
            if (
              auth.isAdministrator() ||
              auth.isModerator() ||
              auth.isContentEditor()
            ) {
              console.log('[Router] Displaying InfluencerList component..');
              return cb(null, InfluencerList);
            } else {
              console.log('[Router] Display PublishedWorkoutsPage component..');
              return cb(null, HomePage);
            }
          },
        },
        {
          path: '/creator/profile',
          getComponent: (nextState, cb) => {
            return cb(null, withLegacyBackground(ProfilePage));
          },
        },
        {
          path: PATHS.CUSTOM_DOMAIN_SETUP(':influencerId'),
          getComponent: (nextState, cb) => {
            return cb(null, withLegacyBackground(DomainSetup));
          },
        },
        {
          childRoutes: [
            {
              path: '/admin/workouts',
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying PublishedWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(PublishedWorkoutsPage));
              },
            },
            {
              path: '/creator/:influencerId/library',
              getComponent: (_, cb) => {
                cb(null, withLegacyBackground(withRouter(LibraryPage)));
              },
            },
            {
              path: '/admin/partner/:influencerId/edit',
              getComponent: (nextState, cb) => {
                cb(null, ProfilePage);
              },
            },
            {
              path: '/admin/partner/:influencerId/admin',
              getComponent: (nextState, cb) => {
                if (auth.isAdministrator() || auth.isModerator()) {
                  cb(null, Administration);
                }
                cb(null, HomePage);
              },
            },
            {
              path: '/admin/partner/:influencerId/settings',
              getComponent: (_, cb) => {
                cb(null, SettingsPage);
              },
            },
            {
              path: PATHS.ADMIN_PAYMENT(':influencerId'),
              getComponent: (_, cb) => {
                cb(null, PayoutsPage);
              },
            },
            {
              path: '/creator/account/profile',
              getComponent: (nextState, cb) => {
                cb(
                  null,
                  withLegacyBackground(
                    withSettingsLayout(ProfilePage, 'profile'),
                  ),
                );
              },
            },
            {
              path: '/creator/account/settings',
              getComponent: (nextState, cb) => {
                cb(
                  null,
                  withLegacyBackground(
                    withSettingsLayout(CreatorAccount, 'account'),
                  ),
                );
              },
            },
            {
              path: PATHS.MARKETING(':influencerId'),
              getComponent: (_, cb) => {
                cb(null, MarketingPage);
              },
            },
            {
              path: PATHS.PROMOTIONS(':influencerId'),
              getComponent: (_, cb) => {
                cb(null, PromotionsPage);
              },
            },

            {
              path: PATHS.PROMOTIONS_DETAILS(':influencerId', ':discountId'),
              getComponent: (_, cb) => {
                cb(null, PromotionsDetailsPage);
              },
            },
            {
              path: PATHS.ANALYTICS,
              getComponent: (nextState, cb) => {
                return cb(null, PlaybookAdminAnalyticsOverview);
              },
            },
            {
              path: PATHS.PARTNER_ANALYTICS(':influencerId'),
              getComponent: (nextState, cb) => {
                return cb(null, AnalyticsOverview);
              },
            },
            {
              path: PATHS.CRM_CREATOR(':influencerId', 'users'),
              getComponent: (nextState, cb) => {
                const canView =
                  auth.isAdministrator() ||
                  auth.isModerator() ||
                  auth.isInfluencer();
                if (canView) {
                  return cb(null, wrappedWithLegacyBackground.CreatorCrmPage);
                }
                return cb(null, withLegacyStyles(NotFoundPage));
              },
            },
            {
              path: PATHS.CRM_CREATOR(':influencerId', 'webhooks'),
              getComponent: (nextState, cb) => {
                const canView =
                  auth.isAdministrator() ||
                  auth.isModerator() ||
                  auth.isInfluencer();
                if (canView) {
                  return cb(null, CrmWebhooksPage);
                }
                return cb(null, withLegacyStyles(NotFoundPage));
              },
            },
            {
              path: PATHS.CHALLENGE_DETAILS(
                ':influencerId',
                ':programId',
                ':challengeId',
              ),
              getComponent: (_, cb) => cb(null, ChallengeDetailsPage),
            },
            {
              path: PATHS.EXTRAS(':influencerId'),
              getComponent: (_, cb) => cb(null, ExtrasPage),
            },
            // {
            //   path: '/admin/old-landing-page/:creatorId',
            //   getComponent: (nextState, cb) => {
            //     return cb(null, withLegacyStyles(CreatorLandingPageConfig));
            //   },
            // },
            {
              path: PATHS.LANDING_PAGE(':influencerId'),
              getComponent: (nextState, cb) => {
                const canView =
                  auth.isAdministrator() ||
                  auth.isModerator() ||
                  auth.isInfluencer();
                if (canView) {
                  return cb(null, LandingPageBuilder);
                }
                return cb(null, withLegacyStyles(NotFoundPage));
              },
            },
            {
              path: '/admin/partner/:influencerId/purchase-page/:programId', // http://localhost:3005/admin/289/purchase-page/297
              getComponent: (nextState, cb) => {
                return cb(null, withLegacyBackground(OTPLandingPageBuilder));
              },
            },
            {
              path: '/admin/config-file',
              getComponent: (nextState, cb) => {
                if (auth.isAdministrator()) {
                  console.log('[Router] Displaying config file component..');
                  return cb(null, withLegacyStyles(ConfigFileEditor));
                }
                return cb(null, InfluencerList);
              },
            },
            {
              path: PATHS.PARTNER_WORKOUTS(':influencerId'),
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying PublishedWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(PublishedWorkoutsPage));
              },
            },
            {
              path: PATHS.PARTNER_WORKOUTS_DRAFTS(':influencerId'),
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying DraftWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(DraftWorkoutsPage));
              },
            },
            {
              path: PATHS.PARTNER_WORKOUTS_SCHEDULED(':influencerId'),
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying ScheduledWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(ScheduledWorkoutsPage));
              },
            },
            {
              path: PATHS.PARTNER_WORKOUTS_PUBLISHED(':influencerId'),
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying PublishedWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(PublishedWorkoutsPage));
              },
            },
            {
              path: '/admin/partner/:influencerId/workouts/published/:programId',
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying PublishedWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(PublishedWorkoutsPage));
              },
            },
            {
              path: '/admin/partner/:influencerId/workouts/archived',
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying ArchivedWorkoutsPage component..',
                );
                cb(null, withLegacyStyles(ArchivedWorkoutsPage));
              },
            },
            {
              path: '/admin/:influencerId/workout/:id/comments',
              getComponent: (nextState, cb) => {
                cb(null, WorkoutCommentList);
              },
            },

            {
              path: '/admin/partner/:influencerId/workout/:id/edit',
              getComponent: (nextState, cb) => {
                cb(null, EditWorkoutPage);
              },
            },
            {
              path: '/admin/partner/:influencerId/class/:id/edit',
              getComponent: (nextState, cb) => {
                console.log('[Router] Displaying WorkoutEdit component..');
                cb(null, EditWorkoutPage);
              },
            },
            {
              path: '/admin/:influencerId/new-program',
              getComponent: (nextState, cb) => {
                console.log(
                  '[Router] Displaying NewCollectionPage component..',
                );
                cb(
                  null,
                  withLegacyTweakedStyles(withRouter(NewCollectionPage)),
                );
              },
            },

            {
              path: '/admin/:influencerId/program/:programId/edit',
              getComponent: (_, cb) =>
                cb(null, withLegacyTweakedStyles(withRouter(CollectionPage))),
            },
            {
              path: '/admin/:influencerId/new-category',
              getComponent: (nextState, cb) => {
                console.log('[Router] Displaying ProgramEdit component..');
                cb(
                  null,
                  withLegacyTweakedStyles(withRouter(NewCollectionPage)),
                );
              },
            },
            {
              path: PATHS.CATEGORY_EDIT(':programId', ':influencerId'),
              getComponent: (nextState, cb) => {
                console.log('[Router] Displaying CategoryEdit component..');
                cb(null, withLegacyTweakedStyles(withRouter(CollectionPage)));
              },
            },
            {
              path: '/admin/workout/:id/view',
              getComponent: (nextState, cb) => {
                console.log('[Router] Displaying WorkoutEdit/View component..');
                cb(null, withLegacyStyles(withRouter(EditWorkoutPage)));
              },
            },
            {
              path: '/admin/portal-users',
              getComponent: (nextState, cb) => {
                if (auth.isAdministrator()) {
                  console.log(
                    '[Router] Displaying PortalUserLists component..',
                  );
                  return cb(null, withLegacyBackground(PortalUserList));
                }
                return cb(null, InfluencerList);
              },
            },
            {
              path: '/admin/new-creator',
              getComponent: (nextState, cb) => {
                if (auth.isAdministrator() || auth.isModerator()) {
                  return cb(null, NewCreator);
                }
                return cb(null, InfluencerList);
              },
            },
            {
              path: '/admin/portal-users/:id/edit',
              getComponent: (nextState, cb) => {
                if (auth.isAdministrator()) {
                  console.log('[Router] Displaying PortalUserEdit component..');
                  return cb(null, withLegacyStyles(PortalUserEdit));
                }
                return cb(null, InfluencerList);
              },
            },
            {
              path: '/admin/livestream',
              getComponent: (nextState, cb) => {
                if (auth.isInfluencer()) {
                  return cb(null, withLegacyStyles(LiveStreamPage));
                } else {
                  return cb(null, withLegacyStyles(NotFoundPage));
                }
              },
            },
            {
              path: '/admin/livestream/upcoming',
              getComponent: (nextState, cb) => {
                if (auth.isInfluencer()) {
                  return cb(null, withLegacyStyles(LiveStreamPage));
                } else {
                  return cb(null, withLegacyStyles(NotFoundPage));
                }
              },
            },
            {
              path: '/admin/livestream/past',
              getComponent: (nextState, cb) => {
                if (auth.isInfluencer()) {
                  return cb(null, withLegacyStyles(LiveStreamPage));
                } else {
                  return cb(null, withLegacyStyles(NotFoundPage));
                }
              },
            },
            {
              path: '/inbox',
              getComponent: (nextState, cb) => {
                if (auth.isInfluencer()) {
                  return cb(null, InboxPage);
                } else {
                  return cb(null, withLegacyStyles(NotFoundPage));
                }
              },
            },
            {
              path: PATHS.INBOX_MESSAGE(':influencerId'),
              getComponent: (nextState, cb) => {
                if (auth.isAdministrator() || auth.isModerator()) {
                  return cb(null, InboxPage);
                } else {
                  return cb(null, withLegacyStyles(NotFoundPage));
                }
              },
            },
            {
              path: '/admin/livestream/:influencerId',
              getComponent: (nextState, cb) => {
                if (
                  auth.isAdministrator() ||
                  auth.isModerator() ||
                  auth.isContentEditor()
                ) {
                  return cb(null, withLegacyStyles(LiveStreamPage));
                } else {
                  return cb(null, withLegacyStyles(NotFoundPage));
                }
              },
            },
            {
              path: '/creator/:influencerId/community',
              getComponent: (nextState, cb) => {
                return cb(null, withLegacyBackground(CommunityInitPage));
              },
            },
            {
              path: '/admin/partner/:influencerId/community',
              getComponent: (nextState, cb) => {
                return cb(null, withLegacyBackground(CommunityInitPage));
              },
            },
            {
              path: '/creator/:influencerId/community/:id',
              getComponent: (nextState, cb) => {
                return cb(null, withLegacyBackground(CommunityPage));
              },
            },
            {
              path: '/creator/:influencerId/community/:communityId/post/:postId',
              getComponent: (nextState, cb) => {
                return cb(null, withLegacyBackground(PostPage));
              },
            },
            {
              path: '/otps(/:influencerId)',
              getComponent: (_, cb) => {
                return cb(null, withLegacyBackground(withRouter(OTPListPage)));
              },
            },
            {
              path: '/subscriptions(/:influencerId)',
              getComponent: (_, cb) => {
                return cb(
                  null,
                  withLegacyBackground(withRouter(SubscriptionListPage)),
                );
              },
            },
            {
              path: '/creator/:influencerId/offers',
              getComponent: (_, cb) => {
                cb(null, withRouter(BundlesManagementPage));
              },
            },
            {
              path: PATHS.OFFER_LANDING_PAGE_EDITOR(
                ':influencerId',
                ':offerId',
              ),
              getComponent: (_, cb) => {
                cb(null, withRouter(OfferLandingPageBuilder));
              },
            },
            {
              path: PATHS.GUIDE(':influencerId'),
              getComponent: (_, cb) => {
                cb(null, withRouter(GuidePage));
              },
            },
          ],
        },
      ],
    },
    {
      path: '*',
      onEnter: (_, replace) => replace(isLoggedIn() ? PATHS.HOME : PATHS.LOGIN),
    },
  ],
};

export const navigate = (path: string) => {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/prev-')) {
    const previewPath = pathname.split('/').slice(0, 2).join('/');
    browserHistory.push(previewPath + path);
  } else {
    browserHistory.push(path);
  }
  return null;
};

export const replacePath = (path: string) => {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/prev-')) {
    const previewPath = pathname.split('/').slice(0, 2).join('/');
    browserHistory.replace(previewPath + path);
  } else {
    browserHistory.replace(path);
  }
  return null;
};

export const goBack = () => {
  browserHistory.goBack();
};
